import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import * as AWS from 'aws-sdk';
import { GetObjectOutput, PutObjectOutput } from 'aws-sdk/clients/s3';
import { orderBy } from 'lodash';
import { POLICY_DOC_SCAN_STATUS, POLICY_DOC_SOURCES } from '../constants/policy-doc';
import { Customer } from '../customer/Customer';
import { Policy } from '../customer/Policy';
import { PolicyDoc } from '../customer/PolicyDoc';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';
import { WorkflowUtils } from '../workflow/WorkflowUtils';
import { S3Service, S3ServiceType } from './S3Service';
import { CoverpathUtilsBackEnd } from '../../../coverpath-common/js/static/CoverpathUtilsBackEnd';

const MODULE = 'RemoteDocService';
const RETRY_ATTEMPTS_INITIAL_VALUE = -1;

export interface RemoteDocQuery {
  docS3_RemoteMIMEType: string;
  id: string;
  name: string;
  date: Date;
  docS3: string;
}

export type RemoteObjectResponse = { [key: string]: { Object: GetObjectOutput; Name: string; mimeType: string } };

@supertypeClass({ toClient: false, toServer: false })
export class RemoteDocService {
  /**
   * @returns true if the customer has permission to read and write to the policy.
   */
  static customerHasPermissions(customer: Customer, policy: Policy): boolean {
    if (customer && customer.application && customer.application.policies) {
      if (customer.application.policies.map((p) => p._id).includes(policy._id)) {
        return true;
      }
    }

    LoggerSingleton.getInstance().error(
      {
        module: MODULE,
        function: 'hasPermissions',
        category: Category.request,

        error: {
          isHumanRelated: false,
        },
      },
      'User is not authorized'
    );

    return false;
  }

  /**
   * @param session - The AmorphicSession object used for logging.
   * @param base64 - base64 of the file
   * @param nameFull
   * @param policy - policy object of type Policy
   * @param appType - visa, I797,
   * @param times - LEAVE DEFAULT VALUE: This is a param as its used in recursive call. Changing this to a higher number is unneccessary computation.
   * @param docType
   * @param uploadedBy
   */
  static uploadFile(
    session: any,
    base64: string,
    nameFull: string,
    policy: Policy,
    appType: string,
    times: number = RETRY_ATTEMPTS_INITIAL_VALUE,
    docType?: string,
    uploadedBy?: string
  ): Promise<void | PutObjectOutput> {
    times++;
    let isDocAddedtoPolicyDoc: boolean = false;
    let docID = '';
    const nameSeps = nameFull.split('.');
    const fileType = nameSeps[1] || ''; //pdf, jpeg
    let name = nameSeps[0];
    name = name.length > 20 ? name.slice(0, 19) : name;
    name = fileType ? `${name}.${fileType}` : name;
    return RemoteDocService.addToPolicyDoc(
      session,
      name,
      fileType,
      policy,
      POLICY_DOC_SOURCES.uploaded,
      appType,
      docType,
      uploadedBy
    )
      .then((id) => {
        if (id) {
          isDocAddedtoPolicyDoc = true;
          docID = id;
          return S3Service.sendToS3(S3ServiceType.RemoteDocService, base64, docID);
        }
        return Promise.reject(`Error adding to Policy Doc and retrieving ID from Policy Doc`);
      })
      .catch((error) => {
        if (times < 3) {
          LoggerSingleton.getInstance().warn(
            {
              module: MODULE,
              function: 'uploadFile',
              category: Category.request,
            },
            `Retrying because ${error}`
          );
          if (isDocAddedtoPolicyDoc) {
            return S3Service.sendToS3(S3ServiceType.RemoteDocService, base64, docID); //Doc is created already. retry one more time
          } else {
            return RemoteDocService.uploadFile(session, base64, nameFull, policy, appType, times); // doc is not created, hence add it first.
          }
        } else {
          if (isDocAddedtoPolicyDoc) {
            return WorkflowUtils.deletePolicyDocs(policy, session, docID).then(() => Promise.reject(error));
          }
          return Promise.reject(error);
        }
      });
  }

  private static async addToPolicyDoc(
    session: any,
    name: string,
    fileType: string,
    policy: Policy,
    src: string,
    appType: string,
    docType?: string,
    uploadedBy?: string
  ): Promise<string> {
    return WorkflowUtils.addPolicyDoc(
      name,
      null,
      fileType,
      src,
      policy,
      appType,
      docType,
      uploadedBy,
      null,
      POLICY_DOC_SCAN_STATUS.PENDING
    )
      .then((data) => {
        if (data) {
          return PolicyDoc.persistorFetchByQuery(
            {
              policyId: policy._id,
              name: name,
              appType: appType,
              isActive: 'true',
            },
            { session: session }
          );
        } else {
          LoggerSingleton.getInstance().error(
            {
              module: MODULE,
              function: 'addToPolicyDoc',
              category: Category.request,
              context: {},
              error: {
                isHumanRelated: false,
              },
            },
            'Not added to policy docs'
          );
          return Promise.reject(`Document ${name} of type ${appType} was not added to policy docs`);
        }
      })
      .then((docs) => {
        return docs[0] && docs[0]._id;
      });
  }

  /**
   * check if doc already uploaded, if so return true, else return false
   * @param session
   * @param policy - policy object of type Policy
   * @param appType - visa, I797,
   * @param getLatest if true, will get the latest from Policy Doc if multiple files.
   * @param policyDocs
   * @param logContextMetadata
   * @returns the body of the object that got uploaded .
   */
  static async downloadObjectAndCleanUp(
    session: any,
    policy: Policy,
    appType: string,
    getLatest: boolean,
    policyDocs?: RemoteDocQuery[]
  ): Promise<RemoteObjectResponse | null> {
    let docs;
    if (policyDocs) {
      docs = policyDocs;
    } else {
      docs = await WorkflowUtils.retrievePolicyDocs(policy, appType, session, [
        POLICY_DOC_SCAN_STATUS.PENDING,
        POLICY_DOC_SCAN_STATUS.CLEAN,
      ]);
    }

    let newDocs: RemoteDocQuery[] = [];
    const docMap = {}; // Map of policyDocID: ETag
    const promises = [];
    if (!docs) {
      return null;
    }

    if (getLatest) {
      const latestDoc = orderBy(docs, 'date', 'desc')[0];
      newDocs.push(latestDoc);
    } else {
      newDocs = docs;
    }

    for (const Doc of newDocs) {
      if (Doc.docS3) {
        const fetchedPolicyDoc = await CoverpathUtilsBackEnd.fetchPolicyDocById(Doc.id);
        if (fetchedPolicyDoc) {
          promises.push(
            (docMap[Doc.id] = {
              Object: fetchedPolicyDoc.docS3,
              Name: Doc.name,
              mimeType: fetchedPolicyDoc.getMIMEFileType(),
            })
          );
        } else {
          LoggerSingleton.getInstance().error(
            {
              module: MODULE,
              function: 'downloadObjectAndCleanUp-notFound-persistor',
              category: Category.request,
            },
            `Skipping adding to docMap as file is not found while using persistor`
          );
        }
      } else {
        promises.push(
          S3Service.downloadFromS3(S3ServiceType.RemoteDocService, Doc.id)
            .then((data: GetObjectOutput) => {
              docMap[Doc.id] = { Object: data.Body, Name: Doc.name, MimeType: data.ContentType };
            })
            .catch((error) =>
              LoggerSingleton.getInstance().error(
                {
                  module: MODULE,
                  function: 'downloadObjectAndCleanUp-notFound',
                  category: Category.request,

                  error: error,
                },
                `${error}-Skipping adding to docMap as file is not found`
              )
            )
        );
      }
    }
    return Promise.all(promises).then(() => Promise.resolve(docMap));
  }

  /**
   * @param key - fileName , it should be unique ( we prefer UUID )
   */
  static getFileContent(key: string): Promise<AWS.S3.Body> {
    return S3Service.downloadFromS3(S3ServiceType.RemoteDocService, key).then((data) => data && data.Body);
  }
}
